import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle, Sparkle, ArrowLeft, CrownSimple } from "@phosphor-icons/react";

const PLAN_DETAILS = {
  pro_monthly: {
    badge: "Más popular",
    title: "Pro Mensual",
    price: "1,99 €",
    period: "/mes",
    description: "Perfecto para probar MIZETA sin compromiso a largo plazo.",
  },
  pro_yearly: {
    badge: "Ahorra 33%",
    title: "Pro Anual",
    price: "15,99 €",
    period: "/año",
    description: "Equivale a 1,33 € al mes. La mejor opción para foodies activos.",
  },
};

const PRO_BENEFITS = [
  "Extracciones ilimitadas de Instagram, TikTok y YouTube",
  "Transcripción y limpieza con IA",
  "Categorías personalizadas sin límite",
  "Modo cocina paso a paso",
  "Edición manual y trucos del chef",
  "Acceso a futuras funciones Pro",
];

export default function Pro() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/status"),
      ]);
      setPlans(p.data.plans);
      setStatus(s.data);
    })();
  }, []);

  const subscribe = async (planId) => {
    setBusy(planId);
    setError("");
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo iniciar el pago.");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="pro-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-8"
        data-testid="pro-back-btn"
      >
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="text-center max-w-3xl mx-auto">
        <p className="eyebrow mb-4 text-[hsl(var(--primary))]">mizeta pro</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-tight">
          Cocina <span className="italic">sin límites</span>.
        </h1>
        <p className="mt-5 text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
          Has usado {status?.extraction_count ?? 0} de {status?.free_limit ?? 5} recetas gratuitas.
          Suscríbete y guarda todas las recetas que encuentres en redes sociales.
        </p>
      </div>

      {status?.is_pro && (
        <div className="mt-10 max-w-2xl mx-auto rounded-2xl bg-[hsl(var(--secondary))]/10 border border-[hsl(var(--secondary))]/30 p-6 flex items-start gap-4" data-testid="pro-active-banner">
          <CrownSimple size={28} weight="fill" className="text-[hsl(var(--secondary))] shrink-0 mt-1" />
          <div>
            <p className="font-serif text-xl">Ya eres MIZETA Pro</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Tu plan {status.active_plan === "pro_yearly" ? "anual" : "mensual"} está activo hasta el{" "}
              <span className="font-semibold text-[hsl(var(--foreground))]">
                {new Date(status.pro_until).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
              </span>.
            </p>
          </div>
        </div>
      )}

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {plans.map((plan) => {
          const meta = PLAN_DETAILS[plan.id] || {};
          const isYearly = plan.id === "pro_yearly";
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-3xl p-8 hover-lift border-2 ${
                isYearly
                  ? "bg-[hsl(var(--foreground))] text-white border-[hsl(var(--foreground))]"
                  : "bg-white border-[hsl(var(--border))]"
              }`}
            >
              {meta.badge && (
                <span className={`absolute -top-3 left-8 px-3 py-1 rounded-full text-xs font-semibold ${
                  isYearly ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]"
                }`}>
                  {meta.badge}
                </span>
              )}

              <p className={`eyebrow ${isYearly ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"}`}>
                {meta.title}
              </p>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-serif text-6xl tracking-tight">{meta.price}</span>
                <span className={isYearly ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}>{meta.period}</span>
              </div>

              <p className={`mt-3 text-sm ${isYearly ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>
                {meta.description}
              </p>

              <button
                onClick={() => subscribe(plan.id)}
                disabled={busy !== null || status?.is_pro}
                data-testid={`subscribe-${plan.id}-btn`}
                className={`mt-6 w-full px-6 py-3 rounded-full font-semibold btn-press inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isYearly
                    ? "bg-[hsl(var(--primary))] text-white"
                    : "bg-[hsl(var(--foreground))] text-white"
                }`}
              >
                <Sparkle size={16} weight="fill" />
                {busy === plan.id ? "Redirigiendo…" : status?.is_pro ? "Ya activo" : "Suscribirme"}
              </button>

              <ul className="mt-7 space-y-3">
                {PRO_BENEFITS.map((b, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <CheckCircle size={18} weight="fill" className={isYearly ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--secondary))]"} />
                    <span className={isYearly ? "text-white/90" : ""}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="mt-6 text-center text-sm text-[hsl(var(--destructive))]" data-testid="pro-error">{error}</p>
      )}

      <div className="mt-16 text-center text-xs text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">
        Pagos seguros con Stripe. Pago único por período. No renovación automática — te avisaremos cuando se acerque el vencimiento.
      </div>
    </div>
  );
}
